// src/components/AddVehicleModal.jsx
import { useEffect, useState } from 'react';
import { Modal, Button, TextInput, NumberInput, Select, Stack, Divider, SimpleGrid, Group, Switch, Textarea } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconCalendar, IconCar, IconCheck, IconX } from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import api from '../../api/axiosConfig';
import 'dayjs/locale/tr';

export default function AddVehicleModal({ opened, close, onVehicleAdded, vehicleToEdit }) {
  const [loading, setLoading] = useState(false);
  
  // --- DİNAMİK VERİ STATE'İ ---
  const [vehicleTypes, setVehicleTypes] = useState([]);

  const form = useForm({
    initialValues: {
      // --- Kimlik ---
      plate: '',                
      brand: '',                
      commercial_type: '',      
      vehicle_type: '', // Dinamik olacağı için başlangıçta boş bırakabilir veya veritabanındaki varsayılan bir değeri yazabilirsin
      model_year: 2024,         
      document_no: '',          

      // --- Durum ---
      registration_date: null,  
      ownership: 'Öz Mal',      
      fuel_type: 'Dizel',       
      current_location: '',      
      allocated_to: '',          
      phone: '',          
      current_km: 0,             

      // --- Yasal ---
      inspection_expiry_date: null, 
      exhaust_emissions_date: null,

      // --- Sigorta ---
      traffic_insurance_expiry: null,     
      traffic_insurance_company: '',      
      traffic_insurance_policy_no: '',    

      // --- Kasko ---
      casco_expiry: null,                 
      casco_company: '',                  
      casco_policy_no: '',

      // --- Sistemler ve Belgeler ---
      details: {
        has_otobil: false,
        otobil_no: '',
        has_hgs: false,
        hgs_no: '',
        has_vehicle_tracking: false,
        has_k_document: false,
        has_d_document: false,
        has_uets: false,
        explanation: '' 
      }
    },
    validate: {
      plate: (value) => (value.length < 5 ? 'Geçerli bir plaka giriniz' : null),
      brand: (value) => (value.length < 2 ? 'Marka boş olamaz' : null),
    },
  });

  // Araç tiplerini backend'den çekme
  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        const response = await api.get('/vehicles/vehicle-types/');
        const formattedOptions = response.data.map(item => ({
          value: item.name,
          label: item.name
        }));
        setVehicleTypes(formattedOptions);
      } catch (error) {
        console.error("Araç tipleri çekilemedi:", error);
      }
    };

    if (opened) {
      fetchVehicleTypes();
    }
  }, [opened]);

  useEffect(() => {
    if (vehicleToEdit) {
      form.setValues({
        ...vehicleToEdit,
        registration_date: vehicleToEdit.registration_date ? new Date(vehicleToEdit.registration_date) : null,
        inspection_expiry_date: vehicleToEdit.inspection_expiry_date ? new Date(vehicleToEdit.inspection_expiry_date) : null,
        exhaust_emissions_date: vehicleToEdit.exhaust_emissions_date ? new Date(vehicleToEdit.exhaust_emissions_date) : null,
        traffic_insurance_expiry: vehicleToEdit.traffic_insurance_expiry ? new Date(vehicleToEdit.traffic_insurance_expiry) : null,
        casco_expiry: vehicleToEdit.casco_expiry ? new Date(vehicleToEdit.casco_expiry) : null,
        phone: vehicleToEdit.phone ? new String(vehicleToEdit.phone) : null,
        details: vehicleToEdit.details || {
          has_otobil: false, otobil_no: '',
          has_hgs: false, hgs_no: '',
          has_vehicle_tracking: false,
          has_k_document: false, has_d_document: false, has_uets: false,
          explanation: '' 
        }
      });
    } else {
      form.reset();
    }
  }, [vehicleToEdit, opened]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      if (vehicleToEdit) {
        await api.put(`/vehicles/${vehicleToEdit.id}`, values);
      } else {
        await api.post('/vehicles/', values);
      }
      
      onVehicleAdded();
      close();
      form.reset();
    } catch (error) {
      console.error("Araç kaydetme hatası:", error);
    } finally {
      setLoading(false);
    }
  };

  const dateProps = {
    locale: "tr",
    valueFormat: "DD.MM.YYYY",
    clearable: true,
    leftSection: <IconCalendar size={16} color="gray" />,
    popoverProps: { withinPortal: true }
  };

  return (
    <Modal 
        opened={opened} 
        onClose={close} 
        title={vehicleToEdit ? "Araç Bilgilerini Düzenle" : "Yeni Araç Ekle"} 
        centered 
        size="xl"
        overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="lg">
          
          {/* --- BÖLÜM 1: ARAÇ KİMLİĞİ --- */}
          <div>
              <Divider label="Araç Ruhsat Bilgileri" labelPosition="left" mb="xs" />
              <SimpleGrid cols={{ base: 1, sm: 3 }}>
                <TextInput label="Plaka" placeholder="34 ABC 12" required fw={700} {...form.getInputProps('plate')} />
                <TextInput label="Marka" placeholder="Renault" required {...form.getInputProps('brand')} />
                <TextInput label="Model / Tip" placeholder="Megane 1.5 Joy" {...form.getInputProps('commercial_type')} />
                
                {/* DİNAMİK SELECT ALANI */}
                <Select 
                  label="Cinsi" 
                  placeholder="Seçiniz"
                  data={vehicleTypes} 
                  searchable
                  clearable
                  {...form.getInputProps('vehicle_type')} 
                />
                
                <NumberInput label="Model Yılı" min={1990} max={2030} {...form.getInputProps('model_year')} />
                <TextInput label="Belge (Ruhsat) No" placeholder="XX123456" {...form.getInputProps('document_no')} />
              </SimpleGrid>
          </div>

          {/* --- BÖLÜM 2: OPERASYONEL --- */}
          <div>
              <Divider label="Operasyonel Durum" labelPosition="left" mb="xs" />
              <SimpleGrid cols={{ base: 1, sm: 3 }}>
                  <NumberInput 
                    label="Mevcut KM" 
                    thousandSeparator="," 
                    required 
                    leftSection={<IconCar size={16}/>}
                    {...form.getInputProps('current_km')} 
                  />
                  <Select label="Yakıt Türü" data={['Dizel', 'Benzin', 'Elektrik', 'Hibrid', 'LPG']} {...form.getInputProps('fuel_type')} />
                  <TextInput label="Mülkiyet" placeholder="Öz Mal / Kiralık" {...form.getInputProps('ownership')} />
                  
                  <TextInput label="Aracın Yeri" placeholder="Şantiye / Merkez Ofis" {...form.getInputProps('current_location')} />
                  <TextInput label="Tahsis Edilen Kişi" placeholder="Personel Adı" {...form.getInputProps('allocated_to')} />
                  <TextInput label="Tahsis Edilen Kişi Tel No:" placeholder="Tel No:" {...form.getInputProps('phone')} />
                  <DatePickerInput label="Tescil Tarihi" placeholder="Tarih Seç" {...dateProps} {...form.getInputProps('registration_date')} />
              </SimpleGrid>
          </div>

          {/* --- BÖLÜM 3: SİGORTA & MUAYENE --- */}
          <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
            <Divider label="Yasal Takip Tarihleri" labelPosition="left" mb="md" />
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
              <Stack gap="sm">
                {/* Muayene ve Egzoz tarihlerini Group grow ile yan yana alıyoruz */}
                <Group grow align="flex-start">
                  <DatePickerInput
                    label="Muayene Bitiş Tarihi"
                    placeholder="Seçiniz"
                    {...dateProps}
                    {...form.getInputProps('inspection_expiry_date')}
                  />
                  <DatePickerInput
                    label="Egzoz Emisyon Bitiş"
                    placeholder="Seçiniz"
                    {...dateProps}
                    {...form.getInputProps('exhaust_emissions_date')}
                  />
                </Group>

                <Divider variant="dotted" />
                <Group grow align="flex-end">
                  <TextInput
                    label="Trafik Sigorta Firması"
                    {...form.getInputProps('traffic_insurance_company')}
                  />
                  <DatePickerInput
                    label="Bitiş Tarihi"
                    {...dateProps}
                    {...form.getInputProps('traffic_insurance_expiry')}
                  />
                </Group>
                <TextInput
                  label="Poliçe No"
                  size="xs"
                  mt={-5}
                  {...form.getInputProps('traffic_insurance_policy_no')}
                />
              </Stack>
              <Stack gap="sm">
                {/* Sol taraftaki Group'un yüksekliğini dengelemek için boş div'i koruyoruz */}
                <div style={{ height: '62px' }}></div>
                <Divider variant="dotted" />
                <Group grow align="flex-end">
                  <TextInput
                    label="Kasko Firması"
                    {...form.getInputProps('casco_company')}
                  />
                  <DatePickerInput
                    label="Bitiş Tarihi"
                    {...dateProps}
                    {...form.getInputProps('casco_expiry')}
                  />
                </Group>
                <TextInput
                  label="Poliçe No"
                  size="xs"
                  mt={-5}
                  {...form.getInputProps('casco_policy_no')}
                />
              </Stack>
            </SimpleGrid>
          </div>

          {/* --- BÖLÜM 4: SİSTEMLER VE BELGELER --- */}
          <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
            <Divider label="Sistemler ve Yasal Belgeler" labelPosition="left" mb="md" />
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
              
              <Stack gap="xs">
                <Switch 
                  label="HGS Bulunuyor" 
                  {...form.getInputProps('details.has_hgs', { type: 'checkbox' })} 
                />
                {form.values.details?.has_hgs && (
                  <TextInput placeholder="HGS Etiket/Plaka No" size="xs" mb="sm" {...form.getInputProps('details.hgs_no')} />
                )}

                <Switch 
                  label="OTOBİL Bulunuyor" 
                  {...form.getInputProps('details.has_otobil', { type: 'checkbox' })} 
                />
                {form.values.details.has_otobil && (
                  <TextInput placeholder="Otobil Cihaz No" size="xs" mb="sm" {...form.getInputProps('details.otobil_no')} />
                )}

                <Switch 
                  label="Araç Takip Sistemi Bulunuyor" 
                  {...form.getInputProps('details.has_vehicle_tracking', { type: 'checkbox' })} 
                />
              </Stack>

              <Stack gap="md">
                <Switch 
                  label="K Belgesi Var" 
                  {...form.getInputProps('details.has_k_document', { type: 'checkbox' })} 
                />
                <Switch 
                  label="D Belgesi Var" 
                  {...form.getInputProps('details.has_d_document', { type: 'checkbox' })} 
                />
                <Switch 
                  label="UTTS Kaydı Var" 
                  {...form.getInputProps('details.has_uets', { type: 'checkbox' })} 
                />
              </Stack>
            </SimpleGrid>

            {/* AÇIKLAMA ALANI */}
            <Textarea
              label="Açıklama / Notlar"
              placeholder="Araçla ilgili eklemek istediğiniz diğer detaylar..."
              minRows={3}
              maxRows={5}
              mt="lg"
              {...form.getInputProps('details.explanation')}
            />
          </div>

          {/* --- BUTONLAR --- */}
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={close} leftSection={<IconX size={16}/>}>
                İptal
            </Button>
            <Button type="submit" size="md" loading={loading} leftSection={<IconCheck size={16}/>}>
                {vehicleToEdit ? "Güncelle" : "Kaydet"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}